// Pure helpers for ContractDetailPage — extracted here to keep the component
// focused on rendering and to allow unit testing without a DOM or React.

// ─── Formatters ───────────────────────────────────────────────────────────────

/** Formats a tonnage value with 2 decimal places using Argentine locale. */
export function tn(v: number): string {
  return v.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Formats a percentage value with 1 decimal place using Argentine locale. */
export function pct(v: number): string {
  return v.toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Formats an ISO date string (YYYY-MM-DD) as "DD/MM" (day/month only).
 * Used for compact period display in table rows.
 */
export function fmtDate(d: string): string {
  const [, m, day] = d.split("-");
  return `${day}/${m}`;
}

/**
 * Formats an ISO date string (YYYY-MM-DD) as "DD/MM/YYYY".
 * Used for full date display in event rows and KPI cards.
 */
export function fmtDateFull(d: string): string {
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

export interface BarSegment {
  label:    string;
  /** Width of this segment as a percentage of the total bar (0–100). */
  widthPct: number;
  color:    string;
  totalTn:  number;
  targetTn: number;
}

/**
 * Converts a list of weekly stats into proportional bar segments for the
 * stacked progress bar. Each segment's width is proportional to its loaded TN
 * relative to the monthly target.
 *
 * @param weeks        Weekly stats (needs weekLabel, totalTn, targetTn).
 * @param monthlyTarget  Monthly TN target — used as 100% denominator.
 * @param colors       Ordered list of Tailwind color classes, cycled by index.
 */
export function computeBarSegments(
  weeks: Array<{ weekLabel: string; totalTn: number; targetTn: number }>,
  monthlyTarget: number,
  colors: string[],
): BarSegment[] {
  const denominator = monthlyTarget > 0 ? monthlyTarget : 1;
  return weeks.map((w, i) => ({
    label:    w.weekLabel,
    widthPct: (w.totalTn / denominator) * 100,
    color:    colors[i % colors.length] ?? "",
    totalTn:  w.totalTn,
    targetTn: w.targetTn,
  }));
}

/**
 * Returns the filled percentage of the monthly target (capped at 100%).
 */
export function computeFilledPct(totalTn: number, monthlyTarget: number): number {
  if (monthlyTarget <= 0) return 0;
  return Math.min(100, (totalTn / monthlyTarget) * 100);
}

/**
 * Returns the remaining (unfilled) percentage of the bar (floor at 0%).
 */
export function computeRemainingPct(filledPct: number): number {
  return Math.max(0, 100 - filledPct);
}

// ─── Week colors ─────────────────────────────────────────────────────────────

export const WEEK_COLORS = [
  "bg-blue-600",
  "bg-emerald-600",
  "bg-violet-600",
  "bg-amber-600",
  "bg-cyan-600",
] as const;

// ─── Month names ─────────────────────────────────────────────────────────────

export const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
] as const;
